"use client";

import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import PAST_EVENTS from "../data/past-events.json";
import EventGallery from "../components/EventGallery";

const EventDetail = () => {
	const { slug } = useParams();
	const [event, setEvent] = useState(null);
	const [loading, setLoading] = useState(true);

	useEffect(() => {
		const foundEvent = PAST_EVENTS.find((e) => e.slug === slug);
		setEvent(foundEvent);
		setLoading(false);
	}, [slug]);

	if (loading) {
		return (
			<div className="min-h-screen bg-white pt-20 flex items-center justify-center">
				<div className="text-center">
					<div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1666ba] mx-auto"></div>
					<p className="mt-4 text-gray-600">Loading event details...</p>
				</div>
			</div>
		);
	}

	if (!event) {
		return (
			<div className="min-h-screen bg-white pt-20 flex items-center justify-center">
				<div className="text-center max-w-md mx-auto px-4">
					<h1 className="text-2xl font-bold text-gray-800 mb-4">Event Not Found</h1>
					<p className="text-gray-600 mb-6">
						The event you're looking for doesn't exist or has been moved.
					</p>
					<Link
						to="/events"
						className="inline-block bg-[#1666ba] text-white px-6 py-3 rounded-full hover:bg-blue-700 transition-colors">
						Back to Events
					</Link>
				</div>
			</div>
		);
	}

	return (
		<div className="bg-white pt-20">
			{/* Hero Section */}
			<section className="relative py-14 sm:py-20 bg-gradient-to-r from-[#1666ba] to-[#819f3d] text-white">
				<div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
					<motion.div
						initial={{ opacity: 0, y: 30 }}
						animate={{ opacity: 1, y: 0 }}
						transition={{ duration: 0.8 }}
						className="text-center">
						<h1 className="text-2xl sm:text-3xl md:text-5xl font-bold mb-4 leading-tight px-2">
							{event.title}
						</h1>
						<p className="text-base sm:text-xl mb-6 opacity-90 px-2">{event.subtitle}</p>

						<div className="flex flex-col sm:flex-row flex-wrap justify-center gap-3 sm:gap-6 text-sm">
							<div className="flex items-center justify-center">
								<svg className="w-5 h-5 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
									<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"
										d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
								</svg>
								<span>{event.date}</span>
							</div>
							<div className="flex items-center justify-center">
								<svg className="w-5 h-5 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
									<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"
										d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
									<path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
								</svg>
								<span>{event.location}</span>
							</div>
						</div>
					</motion.div>
				</div>
			</section>

			{/* About This Event */}
			<section className="py-12 sm:py-16">
				<div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
					<motion.div
						initial={{ opacity: 0, y: 20 }}
						animate={{ opacity: 1, y: 0 }}
						transition={{ duration: 0.6 }}>
						<div className="max-w-4xl mx-auto">
							<h2 className="text-2xl sm:text-3xl font-bold text-[#1666ba] mb-6 text-center">
								About This Event
							</h2>
							<div className="prose prose-lg max-w-none">
								<p className="text-base sm:text-lg text-gray-700 mb-6">
									{event.description}
								</p>
								<div className="whitespace-pre-line text-sm sm:text-base text-gray-700 leading-relaxed">
									{event.longDescription}
								</div>
							</div>
						</div>
					</motion.div>
				</div>
			</section>

			{/* Event Gallery */}
			<EventGallery
				galleryFolder={event.galleryFolder}
				title={`${event.title} — Gallery`}
			/>

			{/* CTA Section */}
			<section className="py-12 sm:py-16 bg-[#1666ba] text-white">
				<div className="max-w-4xl mx-auto text-center px-4 sm:px-6">
					<h2 className="text-2xl sm:text-3xl font-bold mb-4 sm:mb-6">Don't Miss Out!</h2>
					<p className="text-base sm:text-xl mb-6 sm:mb-8 opacity-90">
						Join us for this exceptional event and be part of shaping the future of leadership and diplomacy.
					</p>
					{event.registrationLink && event.registrationLink !== "#" ? (
						<a
							href={event.registrationLink}
							target="_blank"
							rel="noopener noreferrer"
							className="inline-block bg-white text-[#1666ba] px-8 py-3 rounded-full font-semibold hover:bg-gray-100 transition-colors text-base sm:text-lg">
							Secure Your Spot →
						</a>
					) : (
						<Link
							to="/contact"
							className="inline-block bg-white text-[#1666ba] px-8 py-3 rounded-full font-semibold hover:bg-gray-100 transition-colors text-base sm:text-lg">
							Contact Us for More Info →
						</Link>
					)}
				</div>
			</section>
		</div>
	);
};

export default EventDetail;
